// src/index.ts
import Alpine from 'alpinejs'
import intersect from '@alpinejs/intersect'
import { initMediaGrid } from './components/MediaGrid/MediaGrid'
import { fullscreenView } from './components/FullscreenView/FullscreenView'
import { RedditService } from './services/RedditService'
import { CompilationService } from './services/CompilationService'
import { mediaUtils } from './utils/mediaUtils'
import { SearchService } from './services/SearchService'
import { searchBar } from './components/SearchBar/SearchBar'

// Initialize Alpine plugins
Alpine.plugin(intersect)
window.Alpine = Alpine

document.addEventListener('alpine:init', () => {
  // Initialize services
  const redditService = new RedditService()
  const compilationService = new CompilationService()

  // Register main app component
  Alpine.data('app', function() {
    return {
      // State
      subreddit: '',
      darkMode: localStorage.getItem('darkMode') === 'true' || true,
      autoScroll: localStorage.getItem('autoScroll') === 'true' || false,
      wasAutoScroll: false,
      scrollSpeed: localStorage.getItem('scrollSpeed') || 'normal',
      settingsPanelOpen: false,
      scrollInterval: null,
      redditService: null,

      // Initialize all features
      init() {
        this.redditService = redditService

        this.initSearchBar(this.subreddit)
        this.loadCompilationPosts()
        this.initFromUrl().then(() => {
          this.initFullscreenViewFromURL()
        })

        // Handle browser navigation
        window.addEventListener('popstate', (event) => {
          this.initFromUrl()
          this.initFullscreenViewFromURL()
        })

        window.addEventListener('subreddit-changed', ((event: CustomEvent) => {
          this.updateSubreddit(event.detail.subreddit);
        }) as EventListener);
      },

      // URL handling
      async initFromUrl() {
        const search = new URLSearchParams(window.location.search)
        const subredditMatch = search.get('sr')
        if (subredditMatch) {
          this.subreddit = subredditMatch
          await this.fetchPosts()

          // Start autoscrolling when new subreddit is selected
          if (subredditMatch) {
            this.autoScroll = true;
            this.syncAutoScroll();
          }

        }
      },

      async updateSubreddit(sub: string) {
        if (sub === '') return

        this.subreddit = sub
        history.pushState({}, '', `/r?sr=${sub}`)
        this.posts = []
        this.loadNextAfter = null
        this.showDropdown = false
        await this.fetchPosts()

        // Start autoscrolling when new subreddit is selected
        if (sub) {
          this.autoScroll = true;
          this.syncAutoScroll();
        }
      },

      // Settings
      toggleDarkMode() {
        this.darkMode = !this.darkMode
        localStorage.setItem('darkMode', String(this.darkMode))
      },

      toggleAutoScroll() {
        this.autoScroll = !this.autoScroll
        localStorage.setItem('autoScroll', String(this.autoScroll))
        this.syncAutoScroll()
      },

      updateScrollSpeed() {
        localStorage.setItem('scrollSpeed', this.scrollSpeed)
        this.syncAutoScroll()
      },

      syncAutoScroll() {
        if (this.scrollInterval) {
          clearInterval(this.scrollInterval)
        }

        if (this.autoScroll) {
          const speeds = {
            slow: 1,
            normal: 2,
            fast: 4
          }

          this.scrollInterval = setInterval(() => {
            window.scrollBy({
              top: speeds[this.scrollSpeed],
              behavior: 'smooth'
            })
          }, 50)
        }
      },

      // Spread in component functionality
      ...initMediaGrid(),
      ...fullscreenView(),
      ...searchBar(new SearchService),

      // Utility methods from mediaUtils
      ...mediaUtils
    }
  })

  Alpine.data('search', () => ({
    searchQuery: '',
    suggestions: [],
    showDropdown: false,
    loading: false,
    searchTimeout: null,

    init() {
      // No initialization needed
    },

    async handleInput() {
      if (this.searchQuery.length < 2) {
        this.showDropdown = false;
        this.suggestions = [];
        return;
      }

      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      this.showDropdown = true;
      this.loading = true;

      this.searchTimeout = setTimeout(async () => {
        try {
          this.suggestions = await redditService.searchSubreddits(this.searchQuery);
        } catch (error) {
          console.error('Error fetching suggestions:', error);
          this.suggestions = [];
        } finally {
          this.loading = false;
        }
      }, 300);
    },

    selectSubreddit(subreddit: string) {
      this.showDropdown = false;

      if (subreddit !== this.subreddit) {
        // Emit event to update the app
        const event = new CustomEvent('subreddit-changed', {
          detail: { subreddit },
          bubbles: true,
          composed: true
        });
        window.dispatchEvent(event);
      }
    },

    closeDropdown() {
      this.showDropdown = false;
    },
  }));
})

Alpine.data('head', () => ({}));

Alpine.start()
